import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import { setMapViewAction, setPanoViewAction } from "redux/slices/mapbox/mapSlice";
import { clearPanoImage } from "redux/slices/Panorama/list";
import CloseIcon from '@mui/icons-material/Close';

const PanoramaComponent = () => {
  const dispatch = useDispatch();
  const viewerRef = useRef(null);
  const panoImage = useSelector((state) => state.pano);
  const isPano = useSelector((state) => state.mapState.isPano);
  const [loading, setLoading] = useState(true);  // Loading state

  // Don't render if isPano is false or panoImage is not available
  if (!isPano || !panoImage) {
    return null;
  }

  useEffect(() => {
    console.log('Panorama useEffect triggered', { isPano, panoImage });
    
    // Don't initialize if panoImage is not available
    if (!panoImage || (!panoImage.tiles_dir_path && !panoImage.file)) {
      console.warn('Panorama: No panoImage data available', panoImage);
      setLoading(false);
      return;
    }

    const viewerElement = viewerRef.current;
    if (!viewerElement) {
      console.warn('Panorama: Viewer element not available');
      setLoading(false);
      return;
    }

    console.log('Panorama: Initializing viewer with data', {
      tiles_dir_path: panoImage.tiles_dir_path,
      file: panoImage.file
    });

    let viewer = null;
    let MarzipanoLib = null;
    let loadingTimeout = null;
    let cleanup = null;

    (async () => {
      try {
        MarzipanoLib = (await import('marzipano')).default;
        viewer = new MarzipanoLib.Viewer(viewerElement);
        
        // Apply white background to Marzipano viewer and canvas elements
        setTimeout(() => {
          const canvases = viewerElement.querySelectorAll('canvas');
          canvases.forEach(canvas => {
            canvas.style.backgroundColor = 'white';
            canvas.style.background = 'white';
          });
          const containers = viewerElement.querySelectorAll('[class*="mzp"], [class*="marzipano"]');
          containers.forEach(container => {
            container.style.backgroundColor = 'white';
            container.style.background = 'white';
          });
        }, 0);
      } catch (e) {
        console.error('Failed to load marzipano:', e);
        // Fallback to pannellum if marzipano is not available
        try {
          const Pannellum = await import('pannellum');
          if (viewerElement && Pannellum?.default?.viewer && panoImage?.file) {
            const fileUrl = panoImage.file.startsWith('http') 
              ? panoImage.file 
              : `${process.env.REACT_APP_BACKEND_URL}${panoImage.file.startsWith('/') ? '' : '/'}${panoImage.file}`;
            console.log('Panorama: Using Pannellum with file URL', fileUrl);
            Pannellum.default.viewer(viewerElement, {
              type: 'equirectangular',
              panorama: fileUrl,
              autoLoad: true,
            });
            setLoading(false);
          } else {
            console.warn('Panorama: Pannellum viewer not available or no file', {
              hasViewer: !!Pannellum?.default?.viewer,
              hasFile: !!panoImage?.file,
              hasElement: !!viewerElement
            });
            setLoading(false);
          }
        } catch (pannellumError) {
          console.error('Failed to load pannellum:', pannellumError);
          setLoading(false);
        }
        return;
      }

      // If marzipano loaded but no tiles_dir_path, try using file directly
      if (!panoImage.tiles_dir_path && panoImage.file) {
        console.warn('Panorama: No tiles_dir_path, trying to use file directly');
        try {
          const Pannellum = await import('pannellum');
          if (Pannellum?.default?.viewer && panoImage.file) {
            const fileUrl = panoImage.file.startsWith('http') 
              ? panoImage.file 
              : `${process.env.REACT_APP_BACKEND_URL}${panoImage.file.startsWith('/') ? '' : '/'}${panoImage.file}`;
            console.log('Panorama: Using Pannellum fallback with file URL', fileUrl);
            Pannellum.default.viewer(viewerElement, {
              type: 'equirectangular',
              panorama: fileUrl,
              autoLoad: true,
            });
            setLoading(false);
          } else {
            console.warn('Panorama: Pannellum fallback not available');
            setLoading(false);
          }
        } catch (err) {
          console.error('Panorama: Failed to load with pannellum fallback', err);
          setLoading(false);
        }
        return;
      }
      
      // If tiles fail to load, try using file as fallback
      // We'll set this up after trying tiles first
      let fallbackToFile = false;

      // If we have a file URL, prefer using it directly with Pannellum
      // since it's simpler and doesn't require tile authentication
      if (panoImage.file) {
        console.log('Panorama: File URL available, using Pannellum with file');
        try {
          console.log('Panorama: Starting Pannellum import...');
          
          // Import Pannellum CSS (may fail, that's okay)
          try {
            await import('pannellum/build/pannellum.css');
            console.log('Panorama: Pannellum CSS loaded');
          } catch (cssError) {
            console.warn('Panorama: Could not load Pannellum CSS (may not be needed)', cssError);
          }
          
          console.log('Panorama: Importing Pannellum module...');
          const pannellumModule = await import('pannellum');
          console.log('Panorama: Pannellum module imported', pannellumModule);
          console.log('Panorama: Module structure', {
            hasDefault: !!pannellumModule.default,
            defaultType: typeof pannellumModule.default,
            hasViewer: !!pannellumModule.viewer,
            viewerType: typeof pannellumModule.viewer,
            moduleKeys: Object.keys(pannellumModule)
          });
          
          // Pannellum can be imported in different ways
          let pannellumViewer = null;
          if (pannellumModule.default && typeof pannellumModule.default.viewer === 'function') {
            pannellumViewer = pannellumModule.default.viewer;
            console.log('Panorama: Found viewer at default.viewer');
          } else if (typeof pannellumModule.viewer === 'function') {
            pannellumViewer = pannellumModule.viewer;
            console.log('Panorama: Found viewer at viewer');
          } else if (typeof pannellumModule.default === 'function') {
            pannellumViewer = pannellumModule.default;
            console.log('Panorama: Found viewer at default (function)');
          } else if (window.pannellum && typeof window.pannellum.viewer === 'function') {
            pannellumViewer = window.pannellum.viewer;
            console.log('Panorama: Found viewer at window.pannellum.viewer');
          }
          
          if (pannellumViewer) {
            const fileUrl = panoImage.file.startsWith('http') 
              ? panoImage.file 
              : `${process.env.REACT_APP_BACKEND_URL}${panoImage.file.startsWith('/') ? '' : '/'}${panoImage.file}`;
            console.log('Panorama: Using Pannellum with file URL', fileUrl);
            console.log('Panorama: Viewer element', viewerElement, {
              exists: !!viewerElement,
              tagName: viewerElement?.tagName,
              id: viewerElement?.id,
              className: viewerElement?.className
            });
            
            // Destroy marzipano viewer if it was created
            if (viewer) {
              try {
                viewer.destroy();
                console.log('Panorama: Marzipano viewer destroyed');
              } catch (e) {
                console.warn('Panorama: Error destroying marzipano viewer (may already be destroyed)', e);
              }
            }
            
            // Clear the viewer element BEFORE initializing Pannellum
            // (Pannellum will create its own container inside)
            viewerElement.innerHTML = '';
            console.log('Panorama: Viewer element cleared');
            
            console.log('Panorama: Calling pannellumViewer...');
            const pannellumInstance = pannellumViewer(viewerElement, {
              type: 'equirectangular',
              panorama: fileUrl,
              autoLoad: true,
            });
            console.log('Panorama: Pannellum viewer initialized', pannellumInstance);
            
            // Check if image loads
            let checkCount = 0;
            const maxChecks = 20; // 10 seconds max (20 * 500ms)
            
            const checkImageLoad = () => {
              checkCount++;
              const pnlmContainer = viewerElement.querySelector('.pnlm-container');
              const pnlmRenderContainer = viewerElement.querySelector('.pnlm-render-container');
              const pnlmCanvas = viewerElement.querySelector('.pnlm-render-container canvas');
              const pnlmImg = viewerElement.querySelector('.pnlm-render-container img');
              
              const isLoaded = pannellumInstance.isLoaded && pannellumInstance.isLoaded();
              const hasCanvas = !!pnlmCanvas;
              const hasImage = !!pnlmImg;
              const imageLoaded = pnlmImg && pnlmImg.complete && pnlmImg.naturalWidth > 0;
              
              console.log('Panorama: Checking viewer state', {
                checkCount,
                hasContainer: !!pnlmContainer,
                hasRenderContainer: !!pnlmRenderContainer,
                hasCanvas,
                hasImage,
                imageLoaded,
                isLoaded,
                imageSrc: pnlmImg?.src?.substring(0, 100),
                viewerElementChildren: viewerElement.children.length,
                containerStyle: pnlmContainer?.style?.display
              });
              
              if (isLoaded || imageLoaded || hasCanvas) {
                console.log('Panorama: Image loaded successfully!');
                setLoading(false);
              } else if (checkCount < maxChecks) {
                console.log('Panorama: Image not loaded yet, checking again...');
                setTimeout(checkImageLoad, 500);
              } else {
                console.warn('Panorama: Max checks reached, hiding loader anyway');
                setLoading(false);
              }
            };
            
            // Start checking after a short delay to let Pannellum initialize
            setTimeout(checkImageLoad, 500);
            
            return;
          } else {
            console.error('Panorama: Pannellum.viewer not found', {
              hasDefault: !!pannellumModule.default,
              defaultType: typeof pannellumModule.default,
              hasViewer: !!pannellumModule.viewer,
              viewerType: typeof pannellumModule.viewer,
              moduleKeys: Object.keys(pannellumModule),
              windowPannellum: !!window.pannellum
            });
            throw new Error('Pannellum viewer function not found');
          }
        } catch (pannellumError) {
          console.error('Panorama: Failed to load with Pannellum', pannellumError);
          console.error('Panorama: Error details', {
            message: pannellumError?.message,
            stack: pannellumError?.stack,
            name: pannellumError?.name
          });
          // Fall through to try tiles
        }
      }
      
      const dir_path = panoImage.tiles_dir_path;
      if (!dir_path) {
        console.warn('Panorama: No tiles_dir_path available and file failed');
        setLoading(false);
        return;
      }

      const faceMap = {
        b: 'b',
        f: 'f',
        l: 'l',
        r: 'r',
        u: 'u',
        d: 'd',
      };
      const encodedDirPath = encodeURIComponent(dir_path);
      const panoPath = `${process.env.REACT_APP_BACKEND_URL}/api/panorama_image_tiles/?dir_path=${encodedDirPath}&z_value={z}&image_name={f}{y}_{x}.jpg`;
      console.log('Panorama: Tiles path', panoPath);
      
      // Test if a tile can be loaded (with auth if available)
      const testTileUrl = panoPath.replace('{z}', '0').replace('{f}', 'f').replace('{y}', '0').replace('{x}', '0');
      console.log('Panorama: Testing tile URL', testTileUrl);
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Token ${token}` } : {};
      fetch(testTileUrl, { method: 'HEAD', headers })
        .then(response => {
          console.log('Panorama: Tile test response', response.status, response.ok);
          if (!response.ok) {
            console.error('Panorama: Tile test failed', response.status, response.statusText);
            // If tiles fail and we have a file, suggest using file instead
            if (panoImage.file) {
              console.warn('Panorama: Tiles not accessible, consider using file fallback');
            }
          }
        })
        .catch(err => {
          console.error('Panorama: Tile test error', err);
        });
      
      try {
        console.log('Panorama: Creating source with path', panoPath);
        const source = MarzipanoLib.ImageUrlSource.fromString(panoPath, {
          faceMap: faceMap,
        });
        console.log('Panorama: Source created', source);

        const geometry = new MarzipanoLib.CubeGeometry([
          { tileSize: 512, size: 512 },
          { tileSize: 1024, size: 1024 },
        ]);
        console.log('Panorama: Geometry created', geometry);

        const limiter = MarzipanoLib.RectilinearView.limit.traditional(8192, 100 * Math.PI / 180);
        const view = new MarzipanoLib.RectilinearView(null, limiter);
        console.log('Panorama: View created', view);

        const scene = viewer.createScene({
          source: source,
          geometry: geometry,
          view: view,
          webglConstraints: {
            alpha: false,
            depth: false,
            antialias: false,
            preserveDrawingBuffer: false,
            powerPreference: 'high-performance',
          }
        });
        console.log('Panorama: Scene created', scene);

        setLoading(true);
        
        let tilesLoaded = 0;
        let tilesErrored = 0;
        
        const tileLoadedHandler = () => {
          tilesLoaded++;
          console.log('Panorama: Tile loaded', tilesLoaded);
          if (tilesLoaded >= 1) {
            setLoading(false);
          }
        };
        
        const tileErrorHandler = async (event) => {
          tilesErrored++;
          console.error('Panorama: Tile load error', tilesErrored, event);
          // If too many errors, try fallback to file
          if (tilesErrored >= 3 && panoImage.file && !fallbackToFile) {
            console.warn('Panorama: Too many tile errors, trying file fallback');
            fallbackToFile = true;
            try {
              // Destroy current viewer
              if (viewer) {
                viewer.destroy();
              }
              // Try Pannellum with file
              const Pannellum = await import('pannellum');
              if (Pannellum?.default?.viewer) {
                const fileUrl = panoImage.file.startsWith('http') 
                  ? panoImage.file 
                  : `${process.env.REACT_APP_BACKEND_URL}${panoImage.file.startsWith('/') ? '' : '/'}${panoImage.file}`;
                console.log('Panorama: Switching to Pannellum with file', fileUrl);
                Pannellum.default.viewer(viewerElement, {
                  type: 'equirectangular',
                  panorama: fileUrl,
                  autoLoad: true,
                });
                setLoading(false);
              }
            } catch (fallbackError) {
              console.error('Panorama: Fallback to file failed', fallbackError);
              setLoading(false);
            }
          }
        };
        
        const sceneChangeHandler = () => {
          console.log('Panorama: Scene changed');
        };

        source.addEventListener('tileLoaded', tileLoadedHandler);
        source.addEventListener('tileError', tileErrorHandler);
        viewer.addEventListener('sceneChange', sceneChangeHandler);

        // Switch to scene immediately
        try {
          console.log('Panorama: Switching to scene');
          scene.switchTo({ transitionDuration: 0 });
          console.log('Panorama: Scene switch called');
          
          // Set a timeout to hide loading after a reasonable time
          setTimeout(() => {
            if (tilesLoaded === 0) {
              console.warn('Panorama: No tiles loaded yet, but hiding loader');
            }
            setLoading(false);
          }, 2000);
        } catch (switchError) {
          console.error('Panorama: Error in scene switch', switchError);
          setLoading(false);
        }

        loadingTimeout = setTimeout(() => {
          console.warn('Panorama: Loading timeout reached');
          setLoading(false);
        }, 10000);

        cleanup = () => {
          if (loadingTimeout) {
            clearTimeout(loadingTimeout);
          }
          if (source) {
            try {
              source.removeEventListener('tileLoaded', tileLoadedHandler);
              source.removeEventListener('tileError', tileErrorHandler);
            } catch (e) {
              console.warn('Error removing source listeners:', e);
            }
          }
          if (viewer && typeof viewer.destroy === 'function') {
            try {
              if (typeof viewer.removeEventListener === 'function') {
                try {
                  viewer.removeEventListener('sceneChange', sceneChangeHandler);
                } catch (_) {}
              }
              viewer.destroy();
            } catch (_) {
              // ignore destroy errors
            }
          }
        };
      } catch (error) {
        console.error('Error creating panorama scene:', error);
        setLoading(false);
      }
    })();

    return () => {
      if (cleanup) {
        cleanup();
      } else {
        // Fallback cleanup
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
        }
        if (viewer && typeof viewer.destroy === 'function') {
          try {
            viewer.destroy();
          } catch (_) {
            // ignore destroy errors
          }
        }
      }
    };
  }, [panoImage]);

  const handlePanoViewClick = () => {
    dispatch(setPanoViewAction(false));
    dispatch(setMapViewAction({ mapView: true })); // force map visible when pano closes
    dispatch(clearPanoImage());
    // Reset pano marker styling when closing the viewer
    // if (typeof window !== 'undefined' && window.map?.setLayoutProperty) {
    //   try { window.map.setLayoutProperty('pano-points-layer', 'icon-image', 'pano-icon'); } catch (_) {}
    // }
    // Give Mapbox a moment to become visible again, then force resize
    if (typeof window !== 'undefined' && window.map?.resize) {
      setTimeout(() => {
        try { window.map.resize(); } catch (_) {}
      }, 100);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100vh',
      zIndex: 9999, 
      backgroundColor: 'white', 
    }}>
      <Tooltip title='Close Pano View'>
        <IconButton
          onClick={handlePanoViewClick}
          sx={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10000 }}
        >
          <CloseIcon
            sx={{ width: 40, height: 40 }}
            style={{ backgroundColor: "lightgray", color: "darkgray", borderRadius: "20px" }}
          />
        </IconButton>
      </Tooltip>

      {loading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10000,
        }}>
          <CircularProgress color="primary" />
        </div>
      )}

      <div 
        ref={viewerRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          backgroundColor: 'white',
          position: 'relative',
          overflow: 'hidden'
        }} 
      />
    </div>
  );
};

export default PanoramaComponent;
